import axios from 'axios'
import store from '../store'
import { AppStream } from 'aws-sdk'

// axios setup start

const baseURL = process.env.NODE_ENV === 'development' ? 'http://localhost:3007' : 'https://api.gg-bus.com'

const api = axios.create({
  baseURL
})

api.interceptors.request.use(config => {
  const accessToken = localStorage.getItem('accessToken')
  if (accessToken) config.headers.authorization = accessToken

  return config
}, err => {
  console.error('request interceptor', err)
  throw Promise.reject(err)
})

api.interceptors.response.use(async function (response) {
  return response
}, async function (err) {
  if (err.response.status === 403 && err.response.data === 'jwt expired') {
    const refreshToken = localStorage.getItem('refreshToken')
    if (!refreshToken) return Promise.reject(err)

    await store.dispatch('refreshAccessToken')

    try {
      const response = await api.request(err.response.config)
      return response
    } catch (err) {
      console.error(err)
      return Promise.reject(err)
    }
  } else {
    return Promise.reject(err)
  }
})

// axios setup end

// utils start
function throwHttpError (err) {
  if (err.response) {
    const error = new Error(err.response.data)
    error.status = err.response.status
    throw error
  } else {
    throw err
  }
}

function serializeFilter (filters = {}) {
  if (!filters) return ''
  return Object.entries(filters).map(([key, value]) => {
    const encodedValue = encodeURIComponent(value)

    return [key, encodedValue].join('=')
  })
    .join('&')
}

function getApiPath (path) {
  return '/companies/' + store.state.user._cp + '/' + path + '/'
}
// utils end

// auth start
export function updatePassword (currentPassword, newPassword) {
  const body = {
    password: newPassword,
    passwordConfirm: currentPassword
  }

  return api.put('/auth/password', body)
    .then(res => res.data)
    .catch(throwHttpError)
}
export function signin (username, password) {
  const body = { username, password }
  return api.post('/auth/signin', body)
    .then(res => res.data)
    .catch(throwHttpError)
}

export function signout () {
  return api.post('/auth/signout')
    .catch(throwHttpError)
}

export function getUserInfo () {
  return api.get('/auth/user')
    .then(res => res.data)
    .catch(throwHttpError)
}

export function refreshAccessToken (refreshToken) {
  return api.post('/auth/refresh', { refreshToken })
    .then(res => res.data)
    .catch(throwHttpError)
}

// auth end

// day start
export function getDayList (filters = {}, query = {}) {
  const params = {
    ...query,
    filters: serializeFilter(filters)
  }
  return api.get(getApiPath('days'), { params })
    .then(res => res.data)
    .catch(throwHttpError)
}
// day end

// dayOfGroup start
export function getDayOfGroupList (filters = {}, query = {}) {
  const params = {
    ...query,
    filters: serializeFilter(filters)
  }
  return api.get(getApiPath('day-of-groups'), { params })
    .then(res => res.data)
    .catch(throwHttpError)
}
// dayOfGroup end

// drivertrip start
export function getDriverTripList (filters = {}, query = {}) {
  const params = {
    ...query,
    filters: serializeFilter(filters)
  }
  return api.get(getApiPath('driver-trips'), { params })
    .then(res => res.data)
    .catch(throwHttpError)
}
// drivertrip end

// drivertrip start
export function getEventList (filters = {}, query = {}) {
  const params = {
    ...query,
    filters: serializeFilter(filters)
  }
  return api.get(getApiPath('events'), { params })
    .then(res => res.data)
    .catch(throwHttpError)
}
// drivertrip end

// area start
export function getAreaList (filters = {}, query = {}) {
  const params = {
    ...query,
    filters: serializeFilter(filters)
  }

  return api.get(getApiPath('areas'), { params })
    .then(res => res.data)
    .catch(throwHttpError)
}

export function getArea (_id) {
  return api.get(getApiPath('areas') + _id)
    .then(res => res.data)
    .catch(throwHttpError)
}

export function updateArea (_id, data) {
  return api.put(getApiPath('areas') + _id, data)
    .then(res => res.data)
    .catch(throwHttpError)
}

export function createArea (data) {
  return api.post(getApiPath('areas'), data)
    .then(res => res.data)
    .catch(throwHttpError)
}

export function removeArea (_id) {
  return api.delete(getApiPath('areas') + _id)
    .then(res => res.data)
    .catch(throwHttpError)
}
// area end

// group start
export function getGroupList (filters = {}, query = {}) {
  const params = {
    ...query,
    filters: serializeFilter(filters)
  }

  return api.get(getApiPath('groups'), { params })
    .then(res => res.data)
    .catch(throwHttpError)
}

export function getGroup (_id) {
  return api.get(getApiPath('groups') + _id)
    .then(res => res.data)
    .catch(throwHttpError)
}

export function updateGroup (_id, data) {
  return api.put(getApiPath('groups') + _id, data)
    .then(res => res.data)
    .catch(throwHttpError)
}

export function createGroup (data) {
  return api.post(getApiPath('groups'), data)
    .then(res => res.data)
    .catch(throwHttpError)
}
// group end

// plan start
export function getPlanList (filters = {}, query = {}) {
  const params = {
    ...query,
    filters: serializeFilter(filters)
  }

  return api.get(getApiPath('plans'), { params })
    .then(res => res.data)
    .catch(throwHttpError)
}

export function getPlan (_id, query) {
  const params = {
    ...query
  }
  return api.get(getApiPath('plans') + _id, { params })
    .then(res => res.data)
    .catch(throwHttpError)
}

export function updatePlan (_id, data) {
  return api.put(getApiPath('plans') + _id, data)
    .then(res => res.data)
    .catch(throwHttpError)
}

export function createPlan (data) {
  return api.post(getApiPath('plans'), data)
    .then(res => res.data)
    .catch(throwHttpError)
}
// plan end

// driver start
export function getDriverList (filters = {}, query = {}) {
  const params = {
    ...query,
    filters: serializeFilter(filters)
  }

  return api.get(getApiPath('drivers'), { params })
    .then(res => res.data)
    .catch(throwHttpError)
}

export function getDriver (_id) {
  return api.get(getApiPath('drivers') + _id)
    .then(res => res.data)
    .catch(throwHttpError)
}

export function updateDriver (_id, data) {
  return api.put(getApiPath('drivers') + _id, data)
    .then(res => res.data)
    .catch(throwHttpError)
}

export function createDriver (data) {
  return api.post(getApiPath('drivers'), data)
    .then(res => res.data)
    .catch(throwHttpError)
}
// group end

// company start
export function getCompany () {
  return api.get(getApiPath('company'))
    .then(res => res.data)
    .catch(throwHttpError)
}

export function updateCompany (data) {
  return api.put(getApiPath('company'), data)
    .then(res => res.data)
    .catch(throwHttpError)
}
// company end

// device start
export function getDeviceList (filters = {}, query = {}) {
  const params = {
    ...query,
    filters: serializeFilter(filters)
  }

  return api.get(getApiPath('devices'), { params })
    .then(res => res.data)
    .catch(throwHttpError)
}

export function getDevice (_id) {
  return api.get(getApiPath('devices') + _id)
    .then(res => res.data)
    .catch(throwHttpError)
}

export function updateDevice (_id, data) {
  return api.put(getApiPath('devices') + _id, data)
    .then(res => res.data)
    .catch(throwHttpError)
}

export function setISGMode (devices, status) {
  return api.put(getApiPath('devices/isgmode'), {
    devices, status
  })
    .then(res => res.data)
    .catch(throwHttpError)
}

export function setRanges (devices, ranges) {
  return api.put(getApiPath('devices/ranges'), {
    devices, ranges
  })
    .then(res => res.data)
    .catch(throwHttpError)
}

export function setParams (device, params) {
  return api.put(getApiPath('devices/params'), {
    device, params
  })
    .then(res => res.data)
    .catch(throwHttpError)
}
// device end

export function getDevicesCount () {
  return api.get('/devices/count')
    .then(res => res.data)
    .catch(throwHttpError)
}

export function getDaysCount () {
  return api.get('/days/count')
    .then(res => res.data)
    .catch(throwHttpError)
}

export function getAdminDashboard () {
  return api.get('/dashboard')
    .then(res => res.data)
    .catch(throwHttpError)
}

// etas start
export function getETASLogs (filters = {}, query = {}) {
  const params = {
    ...query,
    filters: serializeFilter(filters)
  }

  return api.get(getApiPath('etas'), { params })
    .then(res => res.data)
    .catch(throwHttpError)
}

export async function downloadETASLog (key) {
  const { token } = await api.get(getApiPath('etas') + key)
    .then(res => res.data)
    .catch(() => null)

  if (!token) throw new Error('Permission denied.')

  const filepath = baseURL + '/download/etas?token=' + token
  window.open(filepath, 'blank')
}

export function requestETASLogDownload (from, until, devices) {
  return api.post(getApiPath('etas/downloads'), {
    from, until, devices
  })
    .then(res => res.data)
    .catch(throwHttpError)
}
// etas end
